/**
 * Rules behind the web "new message" notices: which conversation rows count as
 * a fresh incoming message, and when a notice must stay silent because the
 * member is already reading that thread. Free of React and browser globals so
 * every decision is unit-testable.
 */
import type { Conversation } from '@wlb/shared/types/chat';
import { getUsernameDisplay } from '../../utils/usernameDisplay';

/** The pair that identifies "the newest message we have already accounted for". */
export interface ConversationMark {
  lastMessageId: string | null;
  lastMessageAt: number;
}

export type ConversationSnapshot = Map<string, ConversationMark>;

const markOf = (conversation: Conversation): ConversationMark => ({
  lastMessageId:
    conversation.lastMessageId != null ? String(conversation.lastMessageId) : null,
  lastMessageAt: conversation.lastMessageAt ?? 0,
});

export const snapshotConversations = (
  conversations: readonly Conversation[],
): ConversationSnapshot =>
  new Map(conversations.map(conversation => [String(conversation.id), markOf(conversation)]));

/** Newest message timestamp in a list, or 0 when none of the rows carry one. */
export const latestMessageAt = (conversations: readonly Conversation[]): number =>
  conversations.reduce((newest, conversation) => Math.max(newest, conversation.lastMessageAt ?? 0), 0);

// `isMute` and `isMuted` both appear on the payload depending on the endpoint.
const isMuted = (conversation: Conversation): boolean =>
  Boolean(conversation.isMuted || conversation.isMute);

export interface FindNewIncomingOptions {
  /**
   * Newest message timestamp already accounted for across every list seen so
   * far. A row missing from `previous` is only news when it beats this: the
   * list can narrow and widen again (a search, a filter chip, a refresh that
   * answers with a different slice), and rows coming back into view are not
   * new messages.
   */
  watermark?: number;
}

/**
 * Rows whose newest message arrived from someone else since `previous` was
 * taken. A row the member sent to (`lastMessageByMe`) or muted never qualifies,
 * and a conversation missing from `previous` only counts when it is unread and
 * newer than the watermark — otherwise the first list load, or a list that
 * simply widened, would announce the backlog.
 */
export const findNewIncomingMessages = (
  previous: ConversationSnapshot,
  conversations: readonly Conversation[],
  { watermark = 0 }: FindNewIncomingOptions = {},
): Conversation[] =>
  conversations.filter(conversation => {
    if (conversation.lastMessageByMe === true || isMuted(conversation)) return false;

    const mark = markOf(conversation);
    const seen = previous.get(String(conversation.id));
    if (!seen) return Boolean(conversation.isUnread) && mark.lastMessageAt > watermark;

    if (mark.lastMessageId !== null && seen.lastMessageId !== null) {
      return mark.lastMessageId !== seen.lastMessageId;
    }
    return mark.lastMessageAt > seen.lastMessageAt;
  });

export interface SuppressNoticeInput {
  conversationId: string;
  /** The conversation whose thread is on screen, if any. */
  activeConversationId: string | null;
}

/**
 * A message for the thread on screen is already visible, so announcing it is
 * noise. Everything else notifies. A hidden tab announces nothing at all, but
 * that is the caller's rule — it also skips the catch-up sync this decision
 * drives — so visibility is deliberately not an input here.
 */
export const shouldSuppressNotice = ({
  conversationId,
  activeConversationId,
}: SuppressNoticeInput): boolean =>
  activeConversationId !== null && String(conversationId) === String(activeConversationId);

/** Longest preview a toast carries before it is cut short. */
const PREVIEW_LIMIT = 80;

/**
 * `Sender: preview`, matching how the shared push helper composes a group
 * message body. Falls back to the translated generic label when the row
 * carries no preview text (an image or GIF only).
 */
export const buildNoticeMessage = (
  conversation: Conversation,
  fallbackLabel: string,
): string => {
  const who = (
    conversation.groupName ||
    getUsernameDisplay(conversation)
  ).trim();
  const preview = (conversation.lastMessage || '').trim();

  if (!who) return preview || fallbackLabel;
  if (!preview) return `${who}: ${fallbackLabel}`;

  const clipped =
    preview.length > PREVIEW_LIMIT ? `${preview.slice(0, PREVIEW_LIMIT).trimEnd()}…` : preview;
  return `${who}: ${clipped}`;
};

/**
 * Structured notice data for the rich speech-bubble toast. Separated from
 * `buildNoticeMessage` so the flat string form stays available for surfaces
 * that still need it (push helpers, accessibility readers).
 */
export interface NoticeData {
  title: string;
  subtitle: string;
  avatar: string | null;
  avatarSeed: string;
  actionPath: string;
}

/**
 * Build the title, preview, avatar, and navigation path for a conversation's
 * incoming message notice. The title is the sender or group name; the
 * subtitle is the message preview or a generic label for media-only messages.
 */
export const buildNoticeData = (
  conversation: Conversation,
  fallbackLabel: string,
): NoticeData => {
  const title = (
    conversation.groupName ||
    getUsernameDisplay(conversation)
  ).trim() || fallbackLabel;
  const preview = (conversation.lastMessage || '').trim();
  const subtitle =
    preview.length > PREVIEW_LIMIT
      ? `${preview.slice(0, PREVIEW_LIMIT).trimEnd()}…`
      : preview || fallbackLabel;

  return {
    title,
    subtitle,
    avatar: typeof conversation.avatar === 'string' ? conversation.avatar : null,
    avatarSeed: String(conversation.id),
    actionPath: `/messages/${conversation.id}`,
  };
};

/**
 * Conversation id of the thread the route has open. Only `/messages/<id>`
 * counts: the inbox, the details page and `/messages/group/new` are not a
 * thread the member is reading.
 */
export const activeConversationIdFromPath = (pathname: string): string | null => {
  const match = /^\/messages\/([^/]+)\/?$/.exec(pathname);
  const id = match?.[1];
  if (!id || id === 'group') return null;
  try {
    return decodeURIComponent(id);
  } catch {
    // A pasted link can carry malformed percent-encoding, and this decision is
    // taken during render: the raw segment is the safe answer.
    return id;
  }
};
